import { SkipToken, skipToken, useQuery } from '@apollo/client/react';
import { useAuth } from '@domain/auth/AuthContext';
import { isBrowser } from '@lib/environment/isBrowser';
import { reportMykissClientError } from '@lib/errors/client';
import { ServerError } from '@apollo/client/errors';

import { handleApolloErrorOnClient } from './shared';
import { OperationVariables, TypedDocumentNode } from '@apollo/client';

// NOTE: The signature of this hook mimics that of useQuery, so if useQuery changes we should also
// update this.
export function useRutilusQuery<TData = any, TVariables extends OperationVariables = object>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: SkipToken | useQuery.Options<TData, TVariables>,
): useQuery.Result<TData, TVariables, 'empty' | 'complete' | 'streaming', Partial<TVariables>> {
  const { getToken, invalidateAccessToken } = useAuth();

  const shouldSkip = !isBrowser() || options === skipToken || options?.skip === true;
  const queryOptions = shouldSkip
    ? skipToken
    : {
        ...options,
        context: { ...options?.context, getAuthToken: getToken },
      };

  // Call the original Apollo Client useQuery hook
  // We need to cast the result because TypeScript can't properly resolve Apollo's complex overloads
  // when called from within a generic wrapper. The runtime behavior matches our declared type.
  const result = useQuery(query, queryOptions as any) as useQuery.Result<
    TData,
    TVariables,
    'empty' | 'complete' | 'streaming',
    Partial<TVariables>
  >;

  if (result.error) {
    // Apollo 4: Check for 401 errors using ServerError.is() and statusCode
    if (
      (ServerError.is(result.error) && result.error.statusCode === 401) ||
      result.error.message === 'Response not successful: Received status code 401'
    ) {
      invalidateAccessToken();
      reportMykissClientError(new Error('User had invalid access token'), undefined, 'warning');
      result.refetch().catch(reportMykissClientError);
    } else {
      handleApolloErrorOnClient(result.error, query, options as useQuery.Options, 'rutilus');
    }
  }

  return result;
}
